import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

import { RowSelection, useRowSelection } from './useRowSelection';

interface BulkArchivableRow {
    id: string;
    occupancy?: { id: string } | null;
}

export interface BulkArchiveState<T extends BulkArchivableRow> {
    selection: RowSelection;
    selectableIds: string[];
    selectedRows: T[];
    isBulkModalOpen: boolean;
    setIsBulkModalOpen: Dispatch<SetStateAction<boolean>>;
}

export function useBulkArchive<T extends BulkArchivableRow>(
    rows: T[] | undefined,
    isEditMode: boolean,
): BulkArchiveState<T> {
    const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);

    const selectableIds = useMemo(
        () =>
            rows
                ?.filter(
                    (row) =>
                        !row.id.startsWith('temp-') &&
                        !!row.occupancy?.id &&
                        !String(row.occupancy.id).startsWith('temp-'),
                )
                .map((row) => row.id) ?? [],
        [rows],
    );

    const selection = useRowSelection(selectableIds);
    const { exitSelectMode } = selection;

    useEffect(() => {
        if (!isEditMode) {
            exitSelectMode();
        }
    }, [isEditMode, exitSelectMode]);

    const selectedRows = useMemo(
        () => rows?.filter((row) => selection.selectedIds.has(row.id)) ?? [],
        [rows, selection.selectedIds],
    );

    return { selection, selectableIds, selectedRows, isBulkModalOpen, setIsBulkModalOpen };
}
